<template>
  <div
    id="map"
    class="elevation-1"
    type="light"
    style="position: relative"
    oncontextmenu="return false"
  >
    <center-location-button
      @click="centerOnAssetReservationArea"
      :disabled="!assetReservationFeature"
      :viewIsAerial="isAerial"
    />
    <change-map-type-button @click="changeViewType" :viewIsAerial="isAerial" />
  </div>
</template>

<script>
import { managementMapsMixin } from "@/mixins/management/managementMapsMixin";
import { GeoJSON } from "ol/format";
import CenterLocationButton from "@/components/_Atoms/CenterLocationButton";
import ChangeMapTypeButton from "@/components/_Atoms/ChangeMapTypeButton";
import { mapActions } from "vuex";
import { geoserverMapMixin } from "@/mixins/geoserverMapMixin";

export default {
  name: "AssetReservationManagementReservationDetailsMap",

  components: {
    CenterLocationButton,
    ChangeMapTypeButton
  },

  mixins: [managementMapsMixin, geoserverMapMixin],

  props: {
    /**
     * Receives the uuid of the selected asset reservation
     */
    assetReservationUuid: String,

    /**
     * Receives the geoJson of the selected asset reservation
     */
    assetReservationGeoJson: String,

    /**
     * Receives the objects that are associated with the selected reservation asset
     */
    assetReservationObjects: Array
  },

  data() {
    return {
      /**
       * Stores the icons of objects that appear on the map
       */
      objectsImageSource: null,

      /**
       * Stores the geometry of the Reservation asset with the style created to appear
       */
      assetReservationFeature: undefined,

      /**
       * Determines the layers available to appear on the map
       */
      layers: [
        "fc_pathway",
        "fc_manhole",
        "fc_pole",
        "fc_building",
        "fc_closure",
        "fc_cable",
        "fc_slack"
      ]
    };
  },

  watch: {
    /**
     * Whenever the assetReservationGeoJson changes, the addReservationAreaToMap method is called.
     */
    assetReservationGeoJson(newValue) {
      if (newValue && this.vectorSource) {
        this.addReservationAreaToMap();
      }
    },

    /**
     * Whenever the assetReservationObjects changes, the showGeoServerObjects method is called.
     */
    assetReservationObjects(newValue) {
      if (newValue) {
        this.showGeoServerObjects();
      }
    }
  },

  mounted() {
    this.createDefaultStyle();
    this.createMap();
    this.createAerialMapLayer();
    this.vectorLayer.setZIndex(4);

    if (this.assetReservationGeoJson) {
      this.addReservationAreaToMap();
    }

    if (this.assetReservationObjects) {
      this.showGeoServerObjects();
    }
  },

  methods: {
    ...mapActions(["fetchImageFromGeoServer"]),

    /**
     * Add reservation area to map from assetReservationGeoJson variable value
     */
    addReservationAreaToMap() {
      this.assetReservationFeature = this.getObjectFeature(
        this.assetReservationGeoJson
      );
      this.vectorSource.addFeature(this.assetReservationFeature);
      this.centerOnAssetReservationArea();
    },

    /**
     * Return resources needed to show the asset reservation area on the map
     * @param featureGeoJSON
     * @returns {*}
     */
    getObjectFeature(featureGeoJSON) {
      let features = new GeoJSON().readFeatures(featureGeoJSON, {
        featureProjection: "EPSG:3857"
      });
      return features[0];
    },

    /**
     * Show objects obtained by GeoServer on the map
     */
    showGeoServerObjects() {
      this.objectsImageSource = this.createObjectsImageSource();
      this.setGeoserverImageLoadFunction(
        this.objectsImageSource,
        this.$store.getters.getAssetReservationManagementSelectedWorkspaceUUID
      );
      let objectsImageLayer = this.createObjectsImageLayer(
        this.objectsImageSource
      );
      this.map.addLayer(objectsImageLayer);
    },

    /**
     * Centers the map on the asset reservation area
     */
    centerOnAssetReservationArea() {
      let geometry = this.assetReservationFeature.getGeometry();
      let size = this.map.getSize();
      this.map.getView().fit(geometry, size);
    },

    /**
     * Return active layers defines in layers variable
     * @returns {string}
     */
    getActiveLayers() {
      let layers = this.layers.map(
        layer => process.env.GEOSERVER_WORKSPACE + ":" + layer
      );
      return layers.join(",");
    },

    getViewParameters() {
      return "pw_instalation_type:'11'\\,'12'\\,'1'\\,'10'\\,'13'\\,'4'\\,'7'\\,'2'\\,'6'\\,'5'\\,'8'\\,'9'\\,'3'\\,'99'";
    },

    getCQLFilters() {
      let cqlFilter = [];
      let ids = [];
      for (const layer of this.layers) {
        let objects = this.assetReservationObjects.filter(
          item => item.aro_object_layer === layer
        );

        if (objects.length > 0) {
          let layerPrefix = this.$store.getters.getLayerInitials(layer);
          ids = objects.map(object => {
            if (object.asset) {
              return object.asset[layerPrefix + "_id"];
            }
          });
          ids = ids.filter(id => !!id);
        }

        if (ids.length > 0) {
          cqlFilter.push("id in (" + ids.join(",") + ")");
        } else {
          cqlFilter.push("1=0");
        }
      }

      return cqlFilter.join(";");
    }
  }
};
</script>



// WEBPACK FOOTER //
// src/components/AssetReservationManagement/Data/ReservationDetailsMap.vue